import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Alert, Button, Form, Spinner } from "react-bootstrap"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface VisitDailyTrend {
  date: string
  count: number
  withImage: number
  withCoordinates: number
}

interface VisitTopUser {
  userId: string
  userName: string
  visitCount: number
  uniquePlaces: number
  withImage: number
  withCoordinates: number
}

interface VisitTopOrganization {
  organizationId: string
  organizationName: string
  visitCount: number
  uniqueUsers: number
  uniquePlaces: number
}

interface VisitReviewData {
  period: { days: number; since: string; until: string }
  totalPlaces: number
  totalVisits: number
  uniquePlacesVisited: number
  coveragePercent: number
  visitsWithImage: number
  visitsWithoutImage: number
  visitsWithCoordinates: number
  visitsWithoutCoordinates: number
  visitsWithoutPlace: number
  topUsers: VisitTopUser[]
  topOrganizations: VisitTopOrganization[]
  dailyTrend: VisitDailyTrend[]
}

interface OrganizationOption {
  id: string
  name: string
}

interface VisitAuditCandidate {
  visitDate: string
  vehicleId: number
  vehicleName: string
  siteId: string
  siteName: string
  distance: number
  durationSeconds: number
  positionCount: number
  sourceType: string
}

interface VisitAuditPayload {
  organizationId: string
  organizationName: string
  audit: {
    hoursBack: number
    policy: VisitPolicy
    processedPlaces: number
    positionRecordsReviewed: number
    stopRecordsReviewed: number
    missingPositionCandidates: number
    missingStopCandidates: number
    totalMissingCandidates: number
    createdVisits: number
    sampleCandidates: VisitAuditCandidate[]
  }
}

interface VisitPolicy {
  organizationId: string
  maxDistanceMeters: number
  minDurationMinutes: number
  usesDefaultPolicy: boolean
  storageReady: boolean
}

interface VisitPolicyPayload {
  organizationId: string
  organizationName: string
  policy: VisitPolicy
}

interface VisitPolicyIssue {
  visitDate: string
  vehicleId: number
  vehicleName: string
  siteId: string
  siteName: string
  distance: number
  durationSeconds: number
  positionCount: number
  reason: string
  sourceType: string
}

const visitRestoreHourPresets = [168, 720, 2160, 4320, 8760]

interface VisitPolicyReviewPayload {
  organizationId: string
  organizationName: string
  review: {
    daysBack: number
    policy: VisitPolicy
    totalVehicleVisitsReviewed: number
    invalidVehicleVisits: number
    totalMatchedStopsReviewed: number
    invalidMatchedStops: number
    invalidVisitSamples: VisitPolicyIssue[]
    invalidStopSamples: VisitPolicyIssue[]
  }
}

interface GeofenceItem {
  id: string
  organizationId: string
  name: string
  description?: string
  placeId?: string
  sourceFileName?: string
  sourceType?: string
  created: string
  vehicleVisitsCount?: number
}

interface GeofenceImportPayload {
  organizationId: string
  sourceFileName: string
  importedCount: number
  replacedCount: number
  importedNames: string[]
  warnings: string[]
}

interface VisitReviewComponentProps {
  organizationId?: string
}

type CurrentUserLike = {
  OrganizationId?: string
  organizationId?: string
}

const getApiErrorMessage = (error: unknown): string | undefined => {
  const response = (error as { response?: { data?: { response?: string } } })?.response
  return typeof response?.data?.response === "string" ? response.data.response : undefined
}

const VisitReviewComponent = ({ organizationId = "" }: VisitReviewComponentProps) => {
  const { t } = useTranslation("global")
  const user = (parsedUser() || {}) as CurrentUserLike
  const canChooseOrganization = isSuperAdmin(user) && !organizationId
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const [days, setDays] = useState(30)
  const [hoursBack, setHoursBack] = useState(168)
  const [data, setData] = useState<VisitReviewData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(false)
  const [isAuditing, setIsAuditing] = useState(false)
  const [isSavingPolicy, setIsSavingPolicy] = useState(false)
  const [isReviewingPolicy, setIsReviewingPolicy] = useState(false)
  const [auditResult, setAuditResult] = useState<VisitAuditPayload | null>(null)
  const [policyResult, setPolicyResult] = useState<VisitPolicyPayload | null>(null)
  const [policyReviewResult, setPolicyReviewResult] = useState<VisitPolicyReviewPayload | null>(null)
  const [auditError, setAuditError] = useState("")
  const [policyError, setPolicyError] = useState("")
  const [policySuccess, setPolicySuccess] = useState("")
  const [isLoadingGeofences, setIsLoadingGeofences] = useState(false)
  const [isImportingGeofences, setIsImportingGeofences] = useState(false)
  const [isBackfillingGeofenceVisits, setIsBackfillingGeofenceVisits] = useState(false)
  const [geofenceError, setGeofenceError] = useState("")
  const [geofenceSuccess, setGeofenceSuccess] = useState("")
  const [geofenceImportResult, setGeofenceImportResult] = useState<GeofenceImportPayload | null>(null)
  const [geofences, setGeofences] = useState<GeofenceItem[]>([])
  const [replaceExistingGeofences, setReplaceExistingGeofences] = useState(true)
  const [geofenceFile, setGeofenceFile] = useState<File | null>(null)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState(organizationId || userOrgId)
  const [maxDistanceMeters, setMaxDistanceMeters] = useState(30)
  const [minDurationMinutes, setMinDurationMinutes] = useState(10)

  const loadOrganizations = useCallback(async () => {
    if (!canChooseOrganization) return

    try {
      setIsLoadingOrganizations(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status !== "ok") return

      const options = ((res.data.response || []) as OrganizationOption[]).map(item => ({
        id: item.id,
        name: item.name,
      }))

      setOrganizations(options)
      if (!selectedOrgId && options.length > 0) {
        setSelectedOrgId(options[0].id)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrganizations(false)
    }
  }, [canChooseOrganization, selectedOrgId])

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams()
      params.set("days", String(days))
      if (selectedOrgId) {
        params.set("OrganizationId", selectedOrgId)
      }
      const res = await API.getAction("Platform/VisitReview", params.toString())
      if (res.data.status === "ok") {
        setData(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [days, selectedOrgId])

  const loadPolicy = useCallback(async () => {
    if (!selectedOrgId) {
      setPolicyResult(null)
      setPolicyReviewResult(null)
      setPolicyError("")
      setPolicySuccess("")
      return
    }

    try {
      setIsLoadingPolicy(true)
      setPolicyError("")
      const params = new URLSearchParams()
      params.set("OrganizationId", selectedOrgId)
      const res = await API.getAction("Organization/VisitCriteria", params.toString())

      if (res.data.status === "ok") {
        const payload = res.data.response as VisitPolicyPayload
        setPolicyResult(payload)
        setMaxDistanceMeters(payload.policy.maxDistanceMeters)
        setMinDurationMinutes(payload.policy.minDurationMinutes)
        return
      }

      setPolicyError(res.data.response || t("Unable to load visit policy"))
    } catch (ex: unknown) {
      console.error(ex)
      setPolicyError(getApiErrorMessage(ex) || t("Unable to load visit policy"))
    } finally {
      setIsLoadingPolicy(false)
    }
  }, [selectedOrgId, t])

  const runAudit = useCallback(async (saveMissingVisits: boolean) => {
    if (!selectedOrgId) {
      setAuditError(t("Select an organization before running the review"))
      return
    }

    try {
      setIsAuditing(true)
      setAuditError("")
      const res = await API.postAction("Sync/VehicleVisitAudit", {
        organizationId: selectedOrgId,
        hoursBack,
        includePositions: true,
        includeStops: true,
        saveMissingVisits,
      })

      if (res.data.status === "ok") {
        setAuditResult(res.data.response)
        if (saveMissingVisits) {
          await loadData()
        }
        return
      }

      setAuditError(res.data.response || t("Unable to run visit audit"))
    } catch (ex: unknown) {
      console.error(ex)
      setAuditError(getApiErrorMessage(ex) || t("Unable to run visit audit"))
    } finally {
      setIsAuditing(false)
    }
  }, [hoursBack, loadData, selectedOrgId, t])

  const savePolicy = useCallback(async () => {
    if (!selectedOrgId) {
      setPolicyError(t("Select an organization before editing the policy"))
      return
    }

    try {
      setIsSavingPolicy(true)
      setPolicyError("")
      setPolicySuccess("")
      const res = await API.postAction("Organization/VisitCriteria", {
        organizationId: selectedOrgId,
        maxDistanceMeters,
        minDurationMinutes,
      })

      if (res.data.status === "ok") {
        const payload = res.data.response as VisitPolicyPayload
        setPolicyResult(payload)
        setMaxDistanceMeters(payload.policy.maxDistanceMeters)
        setMinDurationMinutes(payload.policy.minDurationMinutes)
        setPolicySuccess(t("Visit policy saved"))
        return
      }

      setPolicyError(res.data.response || t("Unable to save visit policy"))
    } catch (ex: unknown) {
      console.error(ex)
      setPolicyError(getApiErrorMessage(ex) || t("Unable to save visit policy"))
    } finally {
      setIsSavingPolicy(false)
    }
  }, [maxDistanceMeters, minDurationMinutes, selectedOrgId, t])

  const runPolicyReview = useCallback(async () => {
    if (!selectedOrgId) {
      setPolicyError(t("Select an organization before running the policy review"))
      return
    }

    try {
      setIsReviewingPolicy(true)
      setPolicyError("")
      const res = await API.postAction("Sync/VehicleVisitPolicyReview", {
        organizationId: selectedOrgId,
        daysBack: days,
      })

      if (res.data.status === "ok") {
        setPolicyReviewResult(res.data.response)
        return
      }

      setPolicyError(res.data.response || t("Unable to review policy compliance"))
    } catch (ex: unknown) {
      console.error(ex)
      setPolicyError(getApiErrorMessage(ex) || t("Unable to review policy compliance"))
    } finally {
      setIsReviewingPolicy(false)
    }
  }, [days, selectedOrgId, t])

  const loadGeofences = useCallback(async () => {
    if (!selectedOrgId) {
      setGeofences([])
      setGeofenceImportResult(null)
      setGeofenceError("")
      setGeofenceSuccess("")
      return
    }

    try {
      setIsLoadingGeofences(true)
      setGeofenceError("")
      const params = new URLSearchParams()
      params.set("OrganizationId", selectedOrgId)
      const res = await API.getAction("Geofences/List", params.toString())

      if (res.data.status === "ok") {
        setGeofences((res.data.response || []) as GeofenceItem[])
        return
      }

      setGeofenceError(res.data.response || t("Unable to load geofences"))
    } catch (ex: unknown) {
      console.error(ex)
      setGeofenceError(getApiErrorMessage(ex) || t("Unable to load geofences"))
    } finally {
      setIsLoadingGeofences(false)
    }
  }, [selectedOrgId, t])

  const importGeofences = useCallback(async () => {
    if (!selectedOrgId) {
      setGeofenceError(t("Select an organization before importing geofences"))
      return
    }

    if (!geofenceFile) {
      setGeofenceError(t("Choose a KMZ or KML file before importing"))
      return
    }

    try {
      setIsImportingGeofences(true)
      setGeofenceError("")
      setGeofenceSuccess("")

      const formData = new FormData()
      formData.append("file", geofenceFile)

      const params = new URLSearchParams()
      params.set("OrganizationId", selectedOrgId)
      params.set("replaceExisting", String(replaceExistingGeofences))

      const res = await API.postAction(`Geofences/ImportKmz?${params.toString()}`, formData, {
        headers: { accept: "text/plain" },
      })

      if (res.data.status === "ok") {
        const payload = res.data.response as GeofenceImportPayload
        setGeofenceImportResult(payload)
        setGeofenceSuccess(t("Geofences imported successfully"))
        setGeofenceFile(null)
        await loadGeofences()
        return
      }

      setGeofenceError(res.data.response || t("Unable to import geofences"))
    } catch (ex: unknown) {
      console.error(ex)
      setGeofenceError(getApiErrorMessage(ex) || t("Unable to import geofences"))
    } finally {
      setIsImportingGeofences(false)
    }
  }, [geofenceFile, loadGeofences, replaceExistingGeofences, selectedOrgId, t])

  const backfillGeofenceVisits = useCallback(async () => {
    if (!selectedOrgId) {
      setGeofenceError(t("Select an organization before backfilling geofence visits"))
      return
    }

    try {
      setIsBackfillingGeofenceVisits(true)
      setGeofenceError("")
      setGeofenceSuccess("")

      const res = await API.postAction("Geofences/BackfillVehicleVisits", {
        organizationId: selectedOrgId,
      })

      if (res.data.status === "ok") {
        const updatedVisits = Number(res.data.response?.updatedVisits || 0)
        setGeofenceSuccess(`${t("Geofence visit backfill completed")}: ${updatedVisits.toLocaleString()}`)
        await loadGeofences()
        return
      }

      setGeofenceError(res.data.response || t("Unable to backfill geofence visits"))
    } catch (ex: unknown) {
      console.error(ex)
      setGeofenceError(getApiErrorMessage(ex) || t("Unable to backfill geofence visits"))
    } finally {
      setIsBackfillingGeofenceVisits(false)
    }
  }, [loadGeofences, selectedOrgId, t])

  useEffect(() => {
    if (canChooseOrganization) {
      loadOrganizations()
    }
  }, [canChooseOrganization, loadOrganizations])

  useEffect(() => {
    setSelectedOrgId(organizationId || userOrgId)
  }, [organizationId, userOrgId])

  useEffect(() => {
    setAuditResult(null)
    setAuditError("")
    setPolicyResult(null)
    setPolicyReviewResult(null)
    setPolicyError("")
    setPolicySuccess("")
    setGeofences([])
    setGeofenceFile(null)
    setGeofenceImportResult(null)
    setGeofenceError("")
    setGeofenceSuccess("")
  }, [selectedOrgId])

  useEffect(() => {
    loadPolicy()
  }, [loadPolicy])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    loadGeofences()
  }, [loadGeofences])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  if (!data) return null

  const dailyChart = {
    labels: data.dailyTrend.map(item => item.date.slice(5)),
    datasets: [
      {
        label: t("Visits"),
        data: data.dailyTrend.map(item => item.count),
        backgroundColor: "#0d6efd",
        borderRadius: 4,
      },
      {
        label: t("With Image"),
        data: data.dailyTrend.map(item => item.withImage),
        backgroundColor: "#198754",
        borderRadius: 4,
      },
      {
        label: t("With Coordinates"),
        data: data.dailyTrend.map(item => item.withCoordinates),
        backgroundColor: "#6f42c1",
        borderRadius: 4,
      },
    ],
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h5 className="fw-bold mb-0">
            <i className="fas fa-route me-2 text-primary" />
            {t("Visit Review")}
          </h5>
          <small className="text-muted">
            {data.period.since} → {data.period.until}
          </small>
        </div>
        <div className="btn-group">
          {[7, 30, 60, 90].map(option => (
            <button
              key={option}
              className={`btn btn-sm ${days === option ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setDays(option)}
            >
              {option}d
            </button>
          ))}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            {canChooseOrganization && (
              <div style={{ minWidth: 260 }}>
                <label className="form-label small text-muted mb-1">{t("Organization")}</label>
                <Form.Select
                  size="sm"
                  value={selectedOrgId}
                  onChange={e => setSelectedOrgId(e.target.value)}
                  disabled={isLoadingOrganizations || isAuditing || isSavingPolicy || isReviewingPolicy}
                >
                  <option value="">{t("Select organization for indicators")}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Form.Select>
              </div>
            )}
          </div>

          <div className="border rounded p-3 bg-light mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
              <div>
                <div className="fw-semibold">{t("Visit policy by organization")}</div>
                <div className="small text-muted">
                  {policyResult?.organizationName || t("Select an organization before editing the policy")}
                </div>
              </div>
              {policyResult && (
                <span className={`badge ${policyResult.policy.usesDefaultPolicy ? "bg-warning text-dark" : "bg-success"}`}>
                  {policyResult.policy.usesDefaultPolicy ? t("Default policy") : t("Custom policy")}
                </span>
              )}
            </div>

            <div className="d-flex flex-wrap align-items-end gap-2">
              <div style={{ width: 170 }}>
                <label className="form-label small text-muted mb-1">{t("Max distance (mts)")}</label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={5}
                  max={500}
                  value={maxDistanceMeters}
                  onChange={e => setMaxDistanceMeters(Number(e.target.value) || 0)}
                  disabled={!selectedOrgId || isLoadingPolicy || isSavingPolicy}
                />
              </div>
              <div style={{ width: 170 }}>
                <label className="form-label small text-muted mb-1">{t("Min duration (min)")}</label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={1}
                  max={1440}
                  value={minDurationMinutes}
                  onChange={e => setMinDurationMinutes(Number(e.target.value) || 0)}
                  disabled={!selectedOrgId || isLoadingPolicy || isSavingPolicy}
                />
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!selectedOrgId || isLoadingPolicy || isSavingPolicy}
                onClick={loadPolicy}
              >
                {isLoadingPolicy ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-arrows-rotate me-1" />}
                {t("Reload policy")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!selectedOrgId || isLoadingPolicy || isSavingPolicy}
                onClick={savePolicy}
              >
                {isSavingPolicy ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-floppy-disk me-1" />}
                {t("Save policy")}
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={!selectedOrgId || isReviewingPolicy}
                onClick={runPolicyReview}
              >
                {isReviewingPolicy ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-shield-halved me-1" />}
                {t("Review historical compliance")}
              </Button>
            </div>

            {policyResult && !policyResult.policy.storageReady && (
              <Alert variant="warning" className="mt-3 mb-0">
                {t("Visit policy storage is not ready. Apply database.sql before saving custom criteria.")}
              </Alert>
            )}

            {policyError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {policyError}
              </Alert>
            )}

            {policySuccess && (
              <Alert variant="success" className="mt-3 mb-0">
                {policySuccess}
              </Alert>
            )}
          </div>

          <div className="border rounded p-3 mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
              <div>
                <div className="fw-semibold">{t("Organization geofences")}</div>
                <div className="small text-muted">
                  {policyResult?.organizationName || t("Select an organization before managing geofences")}
                </div>
              </div>
              <div className="small text-muted">
                {t("Loaded geofences")}: {geofences.length.toLocaleString()}
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <div style={{ minWidth: 280 }}>
                <label className="form-label small text-muted mb-1">{t("KMZ/KML file")}</label>
                <Form.Control
                  size="sm"
                  type="file"
                  accept=".kmz,.kml"
                  disabled={!selectedOrgId || isImportingGeofences}
                  onChange={e => {
                    const input = e.currentTarget as HTMLInputElement
                    setGeofenceFile(input.files?.[0] || null)
                  }}
                />
              </div>
              <Form.Check
                id="replace-existing-geofences"
                type="checkbox"
                label={t("Replace existing geofences")}
                checked={replaceExistingGeofences}
                disabled={!selectedOrgId || isImportingGeofences}
                onChange={e => setReplaceExistingGeofences(e.target.checked)}
              />
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!selectedOrgId || isLoadingGeofences || isImportingGeofences || isBackfillingGeofenceVisits}
                onClick={loadGeofences}
              >
                {isLoadingGeofences ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-arrows-rotate me-1" />}
                {t("Reload geofences")}
              </Button>
              <Button
                size="sm"
                variant="outline-warning"
                disabled={!selectedOrgId || isImportingGeofences || isBackfillingGeofenceVisits}
                onClick={backfillGeofenceVisits}
              >
                {isBackfillingGeofenceVisits ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-link me-1" />}
                {t("Backfill geofence visits")}
              </Button>
              <Button
                size="sm"
                variant="success"
                disabled={!selectedOrgId || !geofenceFile || isImportingGeofences || isBackfillingGeofenceVisits}
                onClick={importGeofences}
              >
                {isImportingGeofences ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-upload me-1" />}
                {t("Import geofences")}
              </Button>
            </div>

            {geofenceError && (
              <Alert variant="danger" className="mb-3">
                {geofenceError}
              </Alert>
            )}

            {geofenceSuccess && (
              <Alert variant="success" className="mb-3">
                {geofenceSuccess}
              </Alert>
            )}

            {geofenceImportResult && (
              <div className="row g-2 mb-3">
                {[
                  { label: t("Imported geofences"), value: geofenceImportResult.importedCount },
                  { label: t("Replaced geofences"), value: geofenceImportResult.replacedCount },
                  { label: t("Import warnings"), value: geofenceImportResult.warnings.length },
                ].map(item => (
                  <div className="col-md-4 col-6" key={item.label}>
                    <div className="border rounded p-2 bg-light h-100">
                      <div className="small text-muted">{item.label}</div>
                      <div className="fw-bold">{item.value.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
                {geofenceImportResult.warnings.length > 0 && (
                  <div className="col-12">
                    <Alert variant="warning" className="mb-0">
                      {geofenceImportResult.warnings.join(" | ")}
                    </Alert>
                  </div>
                )}
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t("Name")}</th>
                    <th>{t("Linked place")}</th>
                    <th className="text-end">{t("Vehicle Visits")}</th>
                    <th>{t("Source file")}</th>
                    <th>{t("Type")}</th>
                    <th className="text-end">{t("Created")}</th>
                  </tr>
                </thead>
                <tbody>
                  {geofences.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">
                        {t("No geofences loaded")}
                      </td>
                    </tr>
                  ) : geofences.map(item => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.placeId || t("Not linked")}</td>
                      <td className="text-end">{(item.vehicleVisitsCount || 0).toLocaleString()}</td>
                      <td>{item.sourceFileName || "-"}</td>
                      <td className="text-capitalize">{item.sourceType || "-"}</td>
                      <td className="text-end">{new Date(item.created).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-end gap-2">
            <div style={{ width: 140 }}>
              <label className="form-label small text-muted mb-1">{t("Hours back")}</label>
              <Form.Control
                size="sm"
                type="number"
                min={1}
                max={8760}
                value={hoursBack}
                onChange={e => setHoursBack(Number(e.target.value) || 168)}
                disabled={isAuditing}
              />
            </div>
            <div>
              <label className="form-label small text-muted mb-1">{t("Historical window")}</label>
              <div className="d-flex flex-wrap gap-1">
                {visitRestoreHourPresets.map(option => {
                  const daysLabel = Math.round(option / 24)
                  return (
                    <Button
                      key={option}
                      size="sm"
                      variant={hoursBack === option ? "secondary" : "outline-secondary"}
                      disabled={isAuditing}
                      onClick={() => setHoursBack(option)}
                    >
                      {daysLabel}d
                    </Button>
                  )
                })}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              disabled={isAuditing || !selectedOrgId}
              onClick={() => runAudit(false)}
            >
              {isAuditing ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-magnifying-glass me-1" />}
              {t("Analyze missing visits")}
            </Button>
            <Button
              size="sm"
              variant="warning"
              disabled={isAuditing || !selectedOrgId}
              onClick={() => runAudit(true)}
            >
              {isAuditing ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-clock-rotate-left me-1" />}
              {t("Restore lost historical visits")}
            </Button>
          </div>

          <div className="small text-muted mt-2">
            {t("This action rebuilds missing vehicle visits using both place points and geofence polygons within the selected window.")}
          </div>

          {auditError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {auditError}
            </Alert>
          )}

          {auditResult && (
            <div className="mt-3">
              <div className="small text-muted mb-2">
                {auditResult.organizationName} • {t("Hours back")}: {auditResult.audit.hoursBack}
                {" • "}
                {t("Policy")}: {auditResult.audit.policy.maxDistanceMeters}m / {auditResult.audit.policy.minDurationMinutes}min
              </div>
              <div className="row g-2 mb-3">
                {[
                  { label: t("Missing candidates"), value: auditResult.audit.totalMissingCandidates },
                  { label: t("From positions"), value: auditResult.audit.missingPositionCandidates },
                  { label: t("From stops"), value: auditResult.audit.missingStopCandidates },
                  { label: t("Created visits"), value: auditResult.audit.createdVisits },
                ].map(item => (
                  <div className="col-md-3 col-6" key={item.label}>
                    <div className="border rounded p-2 bg-light h-100">
                      <div className="small text-muted">{item.label}</div>
                      <div className="fw-bold">{item.value.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("Date")}</th>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Place")}</th>
                      <th className="text-end">{t("Distance (mts)")}</th>
                      <th className="text-end">{t("Source")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditResult.audit.sampleCandidates.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          {t("No missing visits detected")}
                        </td>
                      </tr>
                    ) : auditResult.audit.sampleCandidates.map(item => (
                      <tr key={`${item.sourceType}-${item.vehicleId}-${item.siteId}-${item.visitDate}`}>
                        <td>{new Date(item.visitDate).toLocaleString()}</td>
                        <td>{item.vehicleName}</td>
                        <td>{item.siteName}</td>
                        <td className="text-end">{Math.round(item.distance)}</td>
                        <td className="text-end text-capitalize">{item.sourceType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {policyReviewResult && (
            <div className="mt-4">
              <div className="small text-muted mb-2">
                {policyReviewResult.organizationName} • {t("Policy")}: {policyReviewResult.review.policy.maxDistanceMeters}m / {policyReviewResult.review.policy.minDurationMinutes}min
              </div>
              <div className="row g-2 mb-3">
                {[
                  { label: t("Vehicle visits reviewed"), value: policyReviewResult.review.totalVehicleVisitsReviewed },
                  { label: t("Invalid vehicle visits"), value: policyReviewResult.review.invalidVehicleVisits },
                  { label: t("Matched stops reviewed"), value: policyReviewResult.review.totalMatchedStopsReviewed },
                  { label: t("Invalid matched stops"), value: policyReviewResult.review.invalidMatchedStops },
                ].map(item => (
                  <div className="col-md-3 col-6" key={item.label}>
                    <div className="border rounded p-2 bg-light h-100">
                      <div className="small text-muted">{item.label}</div>
                      <div className="fw-bold">{item.value.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row g-3">
                <div className="col-lg-6">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("Invalid visits")}</th>
                          <th>{t("Vehicle")}</th>
                          <th>{t("Place")}</th>
                          <th className="text-end">{t("Distance (mts)")}</th>
                          <th className="text-end">{t("Reason")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {policyReviewResult.review.invalidVisitSamples.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-3">
                              {t("No invalid visits found")}
                            </td>
                          </tr>
                        ) : policyReviewResult.review.invalidVisitSamples.map(item => (
                          <tr key={`visit-${item.vehicleId}-${item.siteId}-${item.visitDate}`}>
                            <td>{new Date(item.visitDate).toLocaleString()}</td>
                            <td>{item.vehicleName}</td>
                            <td>{item.siteName}</td>
                            <td className="text-end">{Math.round(item.distance)}</td>
                            <td className="text-end text-capitalize">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("Invalid stops")}</th>
                          <th>{t("Vehicle")}</th>
                          <th>{t("Place")}</th>
                          <th className="text-end">{t("Distance (mts)")}</th>
                          <th className="text-end">{t("Reason")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {policyReviewResult.review.invalidStopSamples.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-3">
                              {t("No invalid stops found")}
                            </td>
                          </tr>
                        ) : policyReviewResult.review.invalidStopSamples.map(item => (
                          <tr key={`stop-${item.vehicleId}-${item.siteId}-${item.visitDate}`}>
                            <td>{new Date(item.visitDate).toLocaleString()}</td>
                            <td>{item.vehicleName || item.vehicleId}</td>
                            <td>{item.siteName || item.siteId}</td>
                            <td className="text-end">{Math.round(item.distance)}</td>
                            <td className="text-end text-capitalize">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row mb-4">
        {[
          { label: t("Visit Coverage"), value: `${data.coveragePercent}%`, icon: "fa-bullseye", color: "#0d6efd" },
          { label: t("Visits"), value: data.totalVisits.toLocaleString(), icon: "fa-walking", color: "#198754" },
          { label: t("With Image"), value: data.visitsWithImage.toLocaleString(), icon: "fa-camera", color: "#fd7e14" },
          { label: t("Without Coordinates"), value: data.visitsWithoutCoordinates.toLocaleString(), icon: "fa-location-crosshairs", color: "#dc3545" },
        ].map((item, index) => (
          <div className="col-md-3 col-sm-6 mb-3" key={index}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 48, height: 48, backgroundColor: item.color + "22", color: item.color }}
                  >
                    <i className={`fas ${item.icon}`} />
                  </div>
                  <div>
                    <div className="fw-bold fs-5">{item.value}</div>
                    <div className="text-muted small">{item.label}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mb-4">
        <div className="col-lg-7 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Visit Registration Trend")}</h6>
              {data.dailyTrend.length > 0 ? (
                <Bar
                  data={dailyChart}
                  options={{
                    responsive: true,
                    plugins: { legend: { position: "top" as const } },
                    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                  }}
                />
              ) : (
                <div className="text-muted small">{t("No data available")}</div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Review Summary")}</h6>
              <div className="small d-flex flex-column gap-2">
                <div className="d-flex justify-content-between"><span>{t("Total Places")}</span><strong>{data.totalPlaces.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("Visited Places")}</span><strong>{data.uniquePlacesVisited.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("With Coordinates")}</span><strong>{data.visitsWithCoordinates.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("Without Image")}</span><strong>{data.visitsWithoutImage.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("Visits Without Place")}</span><strong>{data.visitsWithoutPlace.toLocaleString()}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Visits by User")}</h6>
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>{t("User")}</th>
                    <th className="text-end">{t("Visits")}</th>
                    <th className="text-end">{t("Places")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topUsers.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                  ) : data.topUsers.map((item, index) => (
                    <tr key={item.userId}>
                      <td>{index + 1}</td>
                      <td>{item.userName}</td>
                      <td className="text-end">{item.visitCount.toLocaleString()}</td>
                      <td className="text-end">{item.uniquePlaces.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Visits by Organization")}</h6>
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>{t("Organization")}</th>
                    <th className="text-end">{t("Visits")}</th>
                    <th className="text-end">{t("Users")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topOrganizations.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                  ) : data.topOrganizations.map((item, index) => (
                    <tr key={item.organizationId}>
                      <td>{index + 1}</td>
                      <td>{item.organizationName}</td>
                      <td className="text-end">{item.visitCount.toLocaleString()}</td>
                      <td className="text-end">{item.uniqueUsers.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VisitReviewComponent
